import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Network,
  Plus,
  Trash2,
  Edit2,
  Download,
  Upload,
  Save,
  RefreshCw,
  Maximize2,
  ZoomIn,
  ZoomOut,
  Home,
  Database,
  Share2,
  GitBranch,
  FileText,
  Settings,
  HelpCircle,
} from "lucide-react";
import Button from "./ui/Button";
import { toast } from "react-toastify";
import { api } from "../common/api";

interface MindMapNode {
  id: string;
  label: string;
  type: "table" | "column" | "query" | "note" | "root";
  data?: any;
  position: { x: number; y: number };
  color?: string;
  shape?: "circle" | "box" | "ellipse" | "database" | "diamond";
}

interface MindMapEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
  type?: "relation" | "reference" | "flow";
  color?: string;
}

interface MindMapData {
  nodes: MindMapNode[];
  edges: MindMapEdge[];
}

interface MindMapBuilderProps {
  selectedConnection?: {
    id: string;
    connection_id?: string;
    name: string;
    type: string;
  };
}

export const MindMapBuilder: React.FC<MindMapBuilderProps> = ({
  selectedConnection,
}) => {
  const [mindMapData, setMindMapData] = useState<MindMapData>({
    nodes: [],
    edges: [],
  });
  const [selectedNode, setSelectedNode] = useState<MindMapNode | null>(null);
  const [showNodeDialog, setShowNodeDialog] = useState(false);
  const [showEdgeDialog, setShowEdgeDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [draggingNode, setDraggingNode] = useState<string | null>(null);
  const [nodeDragStart, setNodeDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const [savedMaps, setSavedMaps] = useState<
    Array<{ id: string; name: string; data: MindMapData }>
  >([]);
  const [currentMapName, setCurrentMapName] = useState("Untitled Mind Map");
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedNodes, setHighlightedNodes] = useState<string[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const [showColumns, setShowColumns] = useState(true);

  useEffect(() => {
    loadSavedMaps();
  }, []);

  // Auto-save current map
  useEffect(() => {
    if (mindMapData.nodes.length > 0) {
      const autoSaveKey = "mindmap_autosave";
      localStorage.setItem(
        autoSaveKey,
        JSON.stringify({
          name: currentMapName,
          data: mindMapData,
          timestamp: Date.now(),
        })
      );
    }
  }, [mindMapData, currentMapName]);

  const loadSavedMaps = () => {
    const saved = localStorage.getItem("mindMaps");
    if (saved) {
      setSavedMaps(JSON.parse(saved));
    }

    // Load autosaved map if exists and current map is empty
    if (mindMapData.nodes.length === 0) {
      const autoSaved = localStorage.getItem("mindmap_autosave");
      if (autoSaved) {
        try {
          const { name, data, timestamp } = JSON.parse(autoSaved);
          // Only load if less than 24 hours old
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            setMindMapData(data);
            setCurrentMapName(name);
            toast.info("Restored autosaved mind map");
          }
        } catch (error) {
          console.error("Failed to load autosaved map:", error);
        }
      }
    }
  };

  const saveMindMap = () => {
    const mapId = `map_${Date.now()}`;
    const newMap = {
      id: mapId,
      name: currentMapName,
      data: mindMapData,
    };

    const updated = [...savedMaps, newMap];
    setSavedMaps(updated);
    localStorage.setItem("mindMaps", JSON.stringify(updated));
    toast.success("Mind map saved successfully!");
  };

  const loadMindMap = (map: {
    id: string;
    name: string;
    data: MindMapData;
  }) => {
    setMindMapData(map.data);
    setCurrentMapName(map.name);
    toast.info(`Loaded: ${map.name}`);
  };

  const generateFromSchema = async () => {
    if (!selectedConnection) {
      toast.error("Please select a database connection first");
      return;
    }

    setIsLoading(true);
    try {
      const connectionId =
        selectedConnection.connection_id || selectedConnection.id;

      const tokens = localStorage.getItem("token");
      if (tokens) {
        const parsedTokens = JSON.parse(tokens);
        const accessToken = parsedTokens.accessToken;
        if (accessToken) {
          api.setAuthToken(accessToken);
        }
      }

      const response = await api.extractSchema({ connection_id: connectionId });

      if (response && response.tables) {
        const nodes: MindMapNode[] = [];
        const edges: MindMapEdge[] = [];

        // Create root node at center
        const centerX = 800;
        const centerY = 600;

        nodes.push({
          id: "root",
          label: selectedConnection.name,
          type: "root",
          position: { x: centerX, y: centerY },
          color: "#3b82f6",
          shape: "database",
        });

        // Handle both array and object table formats
        let tableNames: string[] = [];
        let tablesData: Record<string, any> = {};

        if (Array.isArray(response.tables)) {
          // If tables is an array
          tableNames = response.tables.map(
            (t: any) => t.name || t.table_name || t
          );
          response.tables.forEach((tableInfo: any) => {
            const name = tableInfo.name || tableInfo.table_name;
            if (name) {
              tablesData[name] = tableInfo;
            }
          });
        } else {
          // If tables is an object
          tableNames = Object.keys(response.tables);
          tablesData = response.tables;
        }

        // Create table nodes with better spacing
        const tableRadius = 400; // Increased radius for better spacing
        const angleStep = (2 * Math.PI) / tableNames.length;

        tableNames.forEach((tableName, index) => {
          const angle = index * angleStep - Math.PI / 2; // Start from top
          const x = centerX + tableRadius * Math.cos(angle);
          const y = centerY + tableRadius * Math.sin(angle);

          const tableNode: MindMapNode = {
            id: `table_${tableName}`,
            label: tableName,
            type: "table",
            position: { x, y },
            color: "#10b981",
            shape: "box",
            data: tablesData[tableName],
          };

          nodes.push(tableNode);

          // Edge from root to table
          edges.push({
            id: `edge_root_${tableName}`,
            from: "root",
            to: `table_${tableName}`,
            type: "relation",
            color: "#6b7280",
          });

          // Create column nodes (only if showColumns is true)
          const tableInfo = tablesData[tableName];
          if (showColumns && tableInfo && tableInfo.columns) {
            // Handle both object and array column structures
            let columns: string[] = [];
            if (
              typeof tableInfo.columns === "object" &&
              !Array.isArray(tableInfo.columns)
            ) {
              columns = Object.keys(tableInfo.columns);
            } else if (Array.isArray(tableInfo.columns)) {
              columns = tableInfo.columns.map(
                (col: any) => col.name || col.column_name
              );
            }

            const colRadius = 150; // Increased for better spacing
            const colAngleStep = (2 * Math.PI) / Math.max(columns.length, 1);

            // Only show first 3 columns to reduce clutter
            columns.slice(0, 3).forEach((colName, colIndex) => {
              const colAngle = angle + colIndex * colAngleStep;
              const colX = x + colRadius * Math.cos(colAngle);
              const colY = y + colRadius * Math.sin(colAngle); // Get column details
              let colData = null;
              if (
                typeof tableInfo.columns === "object" &&
                !Array.isArray(tableInfo.columns)
              ) {
                colData = tableInfo.columns[colName];
              } else if (Array.isArray(tableInfo.columns)) {
                colData = tableInfo.columns.find(
                  (col: any) => (col.name || col.column_name) === colName
                );
              }

              const columnNode: MindMapNode = {
                id: `col_${tableName}_${colName}`,
                label: colName,
                type: "column",
                position: { x: colX, y: colY },
                color: "#8b5cf6",
                shape: "ellipse",
                data: colData,
              };
              nodes.push(columnNode);

              edges.push({
                id: `edge_${tableName}_${colName}`,
                from: `table_${tableName}`,
                to: `col_${tableName}_${colName}`,
                type: "reference",
                color: "#9ca3af",
              });
            });
          }

          // Detect and add foreign key relationships
          if (tableInfo.columns) {
            const columnsArray =
              typeof tableInfo.columns === "object" &&
              !Array.isArray(tableInfo.columns)
                ? Object.entries(tableInfo.columns).map(([name, info]) => ({
                    name,
                    ...(info as any),
                  }))
                : tableInfo.columns;

            columnsArray?.forEach((col: any) => {
              const colName = col.name || col.column_name;
              const colType = col.type || col.data_type || "";

              // Check if column is a foreign key (common patterns)
              if (
                col.foreign_key ||
                col.references ||
                colName.endsWith("_id") ||
                colName.startsWith("fk_")
              ) {
                // Try to find referenced table
                let referencedTable =
                  col.foreign_key?.table || col.references?.table;

                // If not explicit, try to infer from column name
                if (!referencedTable && colName.endsWith("_id")) {
                  const possibleTable = colName.replace(/_id$/, "");
                  if (tableNames.includes(possibleTable)) {
                    referencedTable = possibleTable;
                  }
                }

                if (referencedTable && tableNames.includes(referencedTable)) {
                  edges.push({
                    id: `fk_${tableName}_${colName}_${referencedTable}`,
                    from: `table_${tableName}`,
                    to: `table_${referencedTable}`,
                    label: colName,
                    type: "relation",
                    color: "#f59e0b",
                  });
                }
              }
            });
          }
        });

        setMindMapData({ nodes, edges });
        // Set initial zoom to fit everything
        setZoom(0.6);
        setPan({ x: 0, y: 0 });
        toast.success(`Generated mind map from ${tableNames.length} tables`);
      }
    } catch (error: any) {
      console.error("Failed to generate mind map:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to generate mind map from schema";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const addNode = (nodeData: Omit<MindMapNode, "id">) => {
    const newNode: MindMapNode = {
      ...nodeData,
      id: `node_${Date.now()}`,
    };
    setMindMapData({
      ...mindMapData,
      nodes: [...mindMapData.nodes, newNode],
    });
    setShowNodeDialog(false);
    toast.success("Node added successfully");
  };

  const removeNode = (nodeId: string) => {
    setMindMapData({
      nodes: mindMapData.nodes.filter((n) => n.id !== nodeId),
      edges: mindMapData.edges.filter(
        (e) => e.from !== nodeId && e.to !== nodeId
      ),
    });
    toast.info("Node removed");
  };

  const addEdge = (from: string, to: string, label?: string) => {
    const newEdge: MindMapEdge = {
      id: `edge_${Date.now()}`,
      from,
      to,
      label,
      type: "relation",
      color: "#6b7280",
    };
    setMindMapData({
      ...mindMapData,
      edges: [...mindMapData.edges, newEdge],
    });
    setShowEdgeDialog(false);
    toast.success("Connection added");
  };

  const removeEdge = (edgeId: string) => {
    setMindMapData({
      ...mindMapData,
      edges: mindMapData.edges.filter((e) => e.id !== edgeId),
    });
    toast.info("Connection removed");
  };

  const exportMindMap = () => {
    const dataStr = JSON.stringify(mindMapData, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${currentMapName}.json`;
    link.click();
    toast.success("Mind map exported as JSON");
  };

  const exportAsImage = () => {
    const svg = canvasRef.current?.querySelector("svg");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${currentMapName}.svg`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Mind map exported as SVG");
  };

  const importMindMap = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          setMindMapData(data);
          toast.success("Mind map imported");
        } catch (error) {
          toast.error("Failed to import mind map");
        }
      };
      reader.readAsText(file);
    }
  };

  const clearMindMap = () => {
    if (confirm("Are you sure you want to clear the mind map?")) {
      setMindMapData({ nodes: [], edges: [] });
      setSearchTerm("");
      setHighlightedNodes([]);
      setSelectedNode(null);
      toast.info("Mind map cleared");
    }
  };

  const autoLayout = () => {
    // Implement force-directed layout or circular layout
    const centerX = 800;
    const centerY = 600;

    // Separate nodes by type
    const rootNodes = mindMapData.nodes.filter((n) => n.type === "root");
    const tableNodes = mindMapData.nodes.filter((n) => n.type === "table");
    const columnNodes = mindMapData.nodes.filter((n) => n.type === "column");
    const otherNodes = mindMapData.nodes.filter(
      (n) => !["root", "table", "column"].includes(n.type)
    );

    const newNodes: MindMapNode[] = [];

    // Position root nodes at center
    rootNodes.forEach((node, i) => {
      newNodes.push({
        ...node,
        position: { x: centerX, y: 100 + i * 100 },
      });
    });

    // Position table nodes in a circle
    const tableRadius = 400;
    tableNodes.forEach((node, i) => {
      const angle = (i / tableNodes.length) * 2 * Math.PI;
      newNodes.push({
        ...node,
        position: {
          x: centerX + tableRadius * Math.cos(angle),
          y: centerY + tableRadius * Math.sin(angle),
        },
      });
    });

    // Position column nodes near their parent tables
    columnNodes.forEach((node) => {
      // Find parent table from edges
      const edge = mindMapData.edges.find((e) => e.to === node.id);
      const parentTable = edge
        ? newNodes.find((n) => n.id === edge.from)
        : null;

      if (parentTable) {
        const colRadius = 180;
        const colIndex = columnNodes
          .filter((cn) => {
            const e = mindMapData.edges.find((ed) => ed.to === cn.id);
            return e?.from === parentTable.id;
          })
          .indexOf(node);
        const angle = (colIndex / 5) * 2 * Math.PI;

        newNodes.push({
          ...node,
          position: {
            x: parentTable.position.x + colRadius * Math.cos(angle),
            y: parentTable.position.y + colRadius * Math.sin(angle),
          },
        });
      } else {
        newNodes.push(node);
      }
    });

    // Keep other nodes as is
    newNodes.push(...otherNodes);

    setMindMapData({
      ...mindMapData,
      nodes: newNodes,
    });

    toast.success("Layout optimized");
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.3));
  const handleResetView = () => {
    setZoom(0.6); // Better initial zoom to see everything
    setPan({ x: 0, y: 0 });
  };

  const toggleColumns = () => {
    setShowColumns(!showColumns);
    // Filter out or show column nodes
    if (showColumns) {
      // Hide columns
      setMindMapData((prev) => ({
        nodes: prev.nodes.filter((n) => n.type !== "column"),
        edges: prev.edges.filter(
          (e) => !e.from.startsWith("col_") && !e.to.startsWith("col_")
        ),
      }));
    } else {
      // Regenerate from schema to show columns again
      toast.info("Regenerate from schema to show columns");
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingNode) {
      handleNodeDrag(e);
    } else if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggingNode(null);
  };

  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    const node = mindMapData.nodes.find((n) => n.id === nodeId);
    if (node) {
      setDraggingNode(nodeId);
      setNodeDragStart({
        x: (e.clientX - pan.x) / zoom - node.position.x,
        y: (e.clientY - pan.y) / zoom - node.position.y,
      });
    }
  };

  const handleNodeDrag = (e: React.MouseEvent) => {
    if (draggingNode) {
      const newX = (e.clientX - pan.x) / zoom - nodeDragStart.x;
      const newY = (e.clientY - pan.y) / zoom - nodeDragStart.y;

      setMindMapData((prev) => ({
        ...prev,
        nodes: prev.nodes.map((node) =>
          node.id === draggingNode
            ? { ...node, position: { x: newX, y: newY } }
            : node
        ),
      }));
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim()) {
      const matches = mindMapData.nodes
        .filter((node) =>
          node.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((node) => node.id);
      setHighlightedNodes(matches);
    } else {
      setHighlightedNodes([]);
    }
  }, [searchTerm, mindMapData.nodes]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete selected node
      if (e.key === "Delete" && selectedNode) {
        removeNode(selectedNode.id);
        setSelectedNode(null);
      }
      // Escape to deselect
      if (e.key === "Escape") {
        setSelectedNode(null);
        setSearchTerm("");
      }
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        saveMindMap();
      }
      // Ctrl/Cmd + Z to reset view
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleResetView();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNode]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Network className="w-6 h-6 text-purple-400" />
            <div>
              <h2 className="text-2xl font-bold">Mind Map Builder</h2>
              <p className="text-sm text-gray-400">
                Visualize database relationships and concepts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={currentMapName}
              onChange={(e) => setCurrentMapName(e.target.value)}
              className="bg-gray-700 rounded px-3 py-1 text-sm"
              placeholder="Map name"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={saveMindMap}
              icon={<Save className="w-4 h-4" />}
            >
              Save
            </Button>
          </div>
        </div>

        {selectedConnection && (
          <div className="bg-gray-700 rounded-lg p-3 flex items-center gap-3">
            <Database className="w-4 h-4 text-green-400" />
            <span className="text-sm">
              Connected to: <strong>{selectedConnection.name}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="bg-gray-800 rounded-lg p-4">
        {/* Statistics */}
        {mindMapData.nodes.length > 0 && (
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400">Total Nodes</div>
              <div className="text-2xl font-bold text-blue-400">
                {mindMapData.nodes.length}
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400">Tables</div>
              <div className="text-2xl font-bold text-green-400">
                {mindMapData.nodes.filter((n) => n.type === "table").length}
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400">Columns</div>
              <div className="text-2xl font-bold text-purple-400">
                {mindMapData.nodes.filter((n) => n.type === "column").length}
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400">Connections</div>
              <div className="text-2xl font-bold text-orange-400">
                {mindMapData.edges.length}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="primary"
            size="sm"
            onClick={generateFromSchema}
            loading={isLoading}
            disabled={!selectedConnection}
            icon={<GitBranch className="w-4 h-4" />}
          >
            Generate from Schema
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowNodeDialog(true)}
            icon={<Plus className="w-4 h-4" />}
          >
            Add Node
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEdgeDialog(true)}
            disabled={mindMapData.nodes.length < 2}
            icon={<Share2 className="w-4 h-4" />}
          >
            Add Connection
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={exportMindMap}
            disabled={mindMapData.nodes.length === 0}
            icon={<Download className="w-4 h-4" />}
          >
            Export JSON
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={exportAsImage}
            disabled={mindMapData.nodes.length === 0}
            icon={<Download className="w-4 h-4" />}
          >
            Export SVG
          </Button>
          <label className="cursor-pointer">
            <Button
              variant="secondary"
              size="sm"
              icon={<Upload className="w-4 h-4" />}
            >
              Import
            </Button>
            <input
              type="file"
              accept=".json"
              onChange={importMindMap}
              className="hidden"
            />
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={clearMindMap}
            disabled={mindMapData.nodes.length === 0}
            icon={<Trash2 className="w-4 h-4" />}
          >
            Clear
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={autoLayout}
            disabled={mindMapData.nodes.length === 0}
            icon={<RefreshCw className="w-4 h-4" />}
          >
            Auto Layout
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={toggleColumns}
            disabled={mindMapData.nodes.length === 0}
            className={showColumns ? "bg-blue-600" : ""}
          >
            {showColumns ? "Hide" : "Show"} Columns
          </Button>

          <div className="ml-auto flex items-center gap-2">
            {/* Search */}
            {mindMapData.nodes.length > 0 && (
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search nodes..."
                className="bg-gray-700 rounded px-3 py-1 text-sm w-48"
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomOut}
              icon={<ZoomOut className="w-4 h-4" />}
            />
            <span className="text-sm px-2">{Math.round(zoom * 100)}%</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleZoomIn}
              icon={<ZoomIn className="w-4 h-4" />}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResetView}
              icon={<Home className="w-4 h-4" />}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowHelp(true)}
              icon={<HelpCircle className="w-4 h-4" />}
            />
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div
        className="bg-gray-800 rounded-lg overflow-hidden"
        style={{ height: "800px" }}
      >
        {/* Legend */}
        {mindMapData.edges.length > 0 && (
          <div className="absolute top-4 right-4 bg-gray-900/90 rounded-lg p-3 z-10">
            <div className="text-xs font-semibold mb-2 text-gray-300">
              Legend
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-orange-500"></div>
                <span className="text-gray-400">Foreign Key</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-gray-500"></div>
                <span className="text-gray-400">Table-Root</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-0.5 bg-gray-500"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg, #6b7280 0, #6b7280 5px, transparent 5px, transparent 10px)",
                  }}
                ></div>
                <span className="text-gray-400">Column Ref</span>
              </div>
            </div>
          </div>
        )}
        <div
          ref={canvasRef}
          className="w-full h-full relative cursor-move bg-gray-900"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 1600 1200"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "center",
            }}
          >
            {/* Grid pattern */}
            <defs>
              <pattern
                id="grid"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="#374151"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />

            {/* Edges */}
            {mindMapData.edges.map((edge) => {
              const fromNode = mindMapData.nodes.find(
                (n) => n.id === edge.from
              );
              const toNode = mindMapData.nodes.find((n) => n.id === edge.to);
              if (!fromNode || !toNode) return null;

              const isFK = edge.type === "relation" && edge.color === "#f59e0b";
              return (
                <g key={edge.id}>
                  <line
                    x1={fromNode.position.x}
                    y1={fromNode.position.y}
                    x2={toNode.position.x}
                    y2={toNode.position.y}
                    stroke={edge.color || "#6b7280"}
                    strokeWidth={isFK ? "3" : "2"}
                    strokeDasharray={
                      isFK ? "" : edge.type === "reference" ? "5,5" : ""
                    }
                    markerEnd="url(#arrowhead)"
                  />
                  {edge.label && (
                    <text
                      x={(fromNode.position.x + toNode.position.x) / 2}
                      y={(fromNode.position.y + toNode.position.y) / 2}
                      fill="#9ca3af"
                      fontSize="12"
                      textAnchor="middle"
                    >
                      {edge.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Arrow marker */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 10 3, 0 6" fill="#6b7280" />
              </marker>
            </defs>

            {/* Nodes */}
            {mindMapData.nodes.map((node) => (
              <g
                key={node.id}
                onClick={() => setSelectedNode(node)}
                onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                className="cursor-move hover:opacity-80 transition-opacity"
                style={{
                  cursor: draggingNode === node.id ? "grabbing" : "grab",
                }}
              >
                {node.shape === "circle" && (
                  <circle
                    cx={node.position.x}
                    cy={node.position.y}
                    r="40"
                    fill={node.color || "#6b7280"}
                    stroke="#fff"
                    strokeWidth="2"
                  />
                )}
                {node.shape === "box" && (
                  <rect
                    x={node.position.x - 60}
                    y={node.position.y - 30}
                    width="120"
                    height="60"
                    rx="8"
                    fill={node.color || "#6b7280"}
                    stroke="#fff"
                    strokeWidth="2"
                  />
                )}
                {node.shape === "ellipse" && (
                  <ellipse
                    cx={node.position.x}
                    cy={node.position.y}
                    rx="60"
                    ry="35"
                    fill={node.color || "#6b7280"}
                    stroke="#fff"
                    strokeWidth="2"
                  />
                )}
                {node.shape === "database" && (
                  <>
                    <ellipse
                      cx={node.position.x}
                      cy={node.position.y - 25}
                      rx="45"
                      ry="15"
                      fill={node.color || "#6b7280"}
                      stroke="#fff"
                      strokeWidth="2"
                    />
                    <rect
                      x={node.position.x - 45}
                      y={node.position.y - 25}
                      width="90"
                      height="50"
                      fill={node.color || "#6b7280"}
                      stroke="none"
                    />
                    <ellipse
                      cx={node.position.x}
                      cy={node.position.y + 25}
                      rx="45"
                      ry="15"
                      fill={node.color || "#6b7280"}
                      stroke="#fff"
                      strokeWidth="2"
                    />
                  </>
                )}
                {node.shape === "diamond" && (
                  <polygon
                    points={`${node.position.x},${node.position.y - 40} ${
                      node.position.x + 50
                    },${node.position.y} ${node.position.x},${
                      node.position.y + 40
                    } ${node.position.x - 50},${node.position.y}`}
                    fill={node.color || "#6b7280"}
                    stroke="#fff"
                    strokeWidth="2"
                  />
                )}
                <text
                  x={node.position.x}
                  y={node.position.y}
                  fill="#fff"
                  fontSize={
                    node.type === "root"
                      ? "16"
                      : node.type === "table"
                      ? "13"
                      : "11"
                  }
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                >
                  {/* Truncate long labels */}
                  {node.label.length > 15
                    ? node.label.substring(0, 13) + "..."
                    : node.label}
                </text>
                {selectedNode?.id === node.id && (
                  <circle
                    cx={node.position.x}
                    cy={node.position.y}
                    r="80"
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    strokeDasharray="5,5"
                  />
                )}
                {highlightedNodes.includes(node.id) &&
                  selectedNode?.id !== node.id && (
                    <circle
                      cx={node.position.x}
                      cy={node.position.y}
                      r="80"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="3"
                      strokeDasharray="5,5"
                      className="animate-pulse"
                    />
                  )}
              </g>
            ))}
          </svg>

          {mindMapData.nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Network className="w-24 h-24 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">No mind map created yet</p>
                <p className="text-sm text-gray-500 mb-4">
                  Generate from schema or add nodes manually
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Node Info Panel */}
      {selectedNode && (
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Node Details: {selectedNode.label}
            </h3>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => removeNode(selectedNode.id)}
                icon={<Trash2 className="w-4 h-4" />}
              >
                Delete
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedNode(null)}
              >
                Close
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-400">Type:</span>
              <p className="font-medium capitalize">{selectedNode.type}</p>
            </div>
            <div>
              <span className="text-sm text-gray-400">Shape:</span>
              <p className="font-medium capitalize">{selectedNode.shape}</p>
            </div>
            <div>
              <span className="text-sm text-gray-400">Color:</span>
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: selectedNode.color }}
                />
                <p className="font-medium">{selectedNode.color}</p>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-400">Position:</span>
              <p className="font-medium">
                X: {Math.round(selectedNode.position.x)}, Y:{" "}
                {Math.round(selectedNode.position.y)}
              </p>
            </div>
          </div>
          {selectedNode.data && (
            <div className="mt-4">
              <span className="text-sm text-gray-400 block mb-2">
                Additional Data:
              </span>
              <div className="bg-gray-900 p-3 rounded space-y-2">
                {typeof selectedNode.data === "object" ? (
                  Object.entries(selectedNode.data).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-start">
                      <span className="text-xs text-gray-500 capitalize">
                        {key}:
                      </span>
                      <span className="text-xs text-gray-300 ml-2">
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </span>
                    </div>
                  ))
                ) : (
                  <pre className="text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedNode.data, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Saved Maps */}
      {savedMaps.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Saved Mind Maps</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {savedMaps.map((map) => (
              <div key={map.id} className="bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2">{map.name}</h4>
                <p className="text-sm text-gray-400 mb-3">
                  {map.data.nodes.length} nodes, {map.data.edges.length}{" "}
                  connections
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => loadMindMap(map)}
                  className="w-full"
                >
                  Load
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dialogs */}
      {showNodeDialog && (
        <AddNodeDialog
          onAdd={addNode}
          onClose={() => setShowNodeDialog(false)}
        />
      )}
      {showEdgeDialog && (
        <AddEdgeDialog
          nodes={mindMapData.nodes}
          onAdd={addEdge}
          onClose={() => setShowEdgeDialog(false)}
        />
      )}
      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}
    </div>
  );
};

const AddNodeDialog: React.FC<{
  onAdd: (node: Omit<MindMapNode, "id">) => void;
  onClose: () => void;
}> = ({ onAdd, onClose }) => {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<MindMapNode["type"]>("note");
  const [color, setColor] = useState("#6b7280");
  const [shape, setShape] = useState<MindMapNode["shape"]>("box");

  const handleSubmit = () => {
    if (!label) {
      toast.error("Please enter a label");
      return;
    }
    onAdd({
      label,
      type,
      color,
      shape,
      position: { x: 400, y: 300 },
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Node</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2"
              placeholder="Node label"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as MindMapNode["type"])}
                className="w-full bg-gray-700 rounded px-3 py-2"
              >
                <option value="note">Note</option>
                <option value="table">Table</option>
                <option value="column">Column</option>
                <option value="query">Query</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Shape</label>
              <select
                value={shape}
                onChange={(e) =>
                  setShape(e.target.value as MindMapNode["shape"])
                }
                className="w-full bg-gray-700 rounded px-3 py-2"
              >
                <option value="box">Box</option>
                <option value="circle">Circle</option>
                <option value="ellipse">Ellipse</option>
                <option value="diamond">Diamond</option>
                <option value="database">Database</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Color</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 bg-gray-700 rounded px-3 py-2"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add Node
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddEdgeDialog: React.FC<{
  nodes: MindMapNode[];
  onAdd: (from: string, to: string, label?: string) => void;
  onClose: () => void;
}> = ({ nodes, onAdd, onClose }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [label, setLabel] = useState("");

  const handleSubmit = () => {
    if (!from || !to) {
      toast.error("Please select both nodes");
      return;
    }
    if (from === to) {
      toast.error("Cannot connect a node to itself");
      return;
    }
    onAdd(from, to, label);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-md space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold">Add Connection</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">From Node</label>
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2"
            >
              <option value="">Select node...</option>
              {nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">To Node</label>
            <select
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2"
            >
              <option value="">Select node...</option>
              {nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Label (Optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full bg-gray-700 rounded px-3 py-2"
              placeholder="Connection label"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Add Connection
          </Button>
        </div>
      </div>
    </div>
  );
};

const HelpDialog: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-blue-400" />
            Mind Map Builder - Help
          </h3>
          <Button variant="secondary" onClick={onClose} size="sm">
            Close
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-lg mb-2 text-blue-400">
              Getting Started
            </h4>
            <ul className="space-y-1 text-sm text-gray-300">
              <li>• Select a database connection from the dropdown</li>
              <li>
                • Click "Generate from Schema" to auto-create a mind map from
                your database
              </li>
              <li>
                • Manually add nodes and connections using the toolbar buttons
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2 text-green-400">
              Keyboard Shortcuts
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between bg-gray-700 p-2 rounded">
                <span className="text-gray-400">Delete Node:</span>
                <kbd className="bg-gray-900 px-2 py-1 rounded">Delete</kbd>
              </div>
              <div className="flex justify-between bg-gray-700 p-2 rounded">
                <span className="text-gray-400">Deselect/Clear:</span>
                <kbd className="bg-gray-900 px-2 py-1 rounded">Esc</kbd>
              </div>
              <div className="flex justify-between bg-gray-700 p-2 rounded">
                <span className="text-gray-400">Save Map:</span>
                <kbd className="bg-gray-900 px-2 py-1 rounded">Ctrl+S</kbd>
              </div>
              <div className="flex justify-between bg-gray-700 p-2 rounded">
                <span className="text-gray-400">Reset View:</span>
                <kbd className="bg-gray-900 px-2 py-1 rounded">Ctrl+Z</kbd>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2 text-purple-400">
              Navigation
            </h4>
            <ul className="space-y-1 text-sm text-gray-300">
              <li>
                • <strong>Pan:</strong> Click and drag on the canvas background
              </li>
              <li>
                • <strong>Zoom:</strong> Use zoom buttons or mouse wheel
              </li>
              <li>
                • <strong>Move Nodes:</strong> Click and drag individual nodes
              </li>
              <li>
                • <strong>Select Node:</strong> Click on a node to view details
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2 text-orange-400">
              Features
            </h4>
            <ul className="space-y-1 text-sm text-gray-300">
              <li>
                • <strong>Auto Layout:</strong> Automatically reorganize nodes
                for better visibility
              </li>
              <li>
                • <strong>Search:</strong> Find nodes by typing in the search
                box
              </li>
              <li>
                • <strong>Export:</strong> Save as JSON (editable) or SVG
                (image)
              </li>
              <li>
                • <strong>Auto-save:</strong> Your work is automatically saved
                locally
              </li>
              <li>
                • <strong>Foreign Keys:</strong> Orange lines show database
                relationships
              </li>
            </ul>
          </div>

          <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-700">
            <p className="text-sm text-blue-200">
              💡 <strong>Tip:</strong> The mind map automatically detects
              foreign key relationships and displays them with orange connector
              lines. This helps you visualize how your database tables are
              related.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
